import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatusPill, Loader, NameHint } from "@/components/ui-bits";
import { useNameLookup } from "@/hooks/useNameLookup";
import { Check, X, Wallet, Plus, Send, Loader2 } from "lucide-react";

export default function AdminApprovals() {
  const [tab, setTab] = useState("deposits");
  const [deposits, setDeposits] = useState(null);
  const [withdrawals, setWithdrawals] = useState(null);
  const [fund, setFund] = useState(null);
  const [addAmt, setAddAmt] = useState("");
  const [send, setSend] = useState({ referral_code: "", amount: "", target: "deposit_wallet" });
  const [busy, setBusy] = useState("");
  const sendLookup = useNameLookup(send.referral_code);

  const load = () => {
    API.get("/admin/deposits").then((r) => setDeposits(r.data.deposits));
    API.get("/admin/withdrawals").then((r) => setWithdrawals(r.data.withdrawals));
    API.get("/admin/fund").then((r) => setFund(r.data.admin_fund));
  };
  useEffect(() => { load(); }, []);

  const addFund = async () => {
    setBusy("add");
    try { const r = await API.post("/admin/fund/add", { amount: parseFloat(addAmt) }); toast.success(r.data.message); setAddAmt(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const sendFund = async () => {
    setBusy("send");
    try { const r = await API.post("/admin/fund/send", { referral_code: send.referral_code, amount: parseFloat(send.amount), target: send.target }); toast.success(r.data.message); setSend({ referral_code: "", amount: "", target: "deposit_wallet" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const act = async (url, msg) => {
    try { const r = await API.post(url); toast.success(r.data.message || msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (url) => {
    const note = window.prompt("Rejection note (optional):") || "";
    try { const r = await API.post(url, { note }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const list = tab === "deposits" ? deposits : withdrawals;
  const field = "w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white outline-none focus:border-emerald-500/50";

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Fund the Admin Fund, send funds to users, and process pending requests. Deposit approvals draw from the Admin Fund." testid="approvals-header" />

      <div className="glass rounded-2xl p-6 mb-6" data-testid="admin-fund-panel">
        <div className="grid md:grid-cols-3 gap-6 items-end">
          <div>
            <div className="flex items-center gap-2 text-amber-300 mb-1"><Wallet className="h-5 w-5" /><span className="text-xs uppercase tracking-wide">Admin Fund</span></div>
            <div className="font-num text-4xl font-black text-white" data-testid="admin-fund-balance">{fund === null ? "…" : fmt(fund)}</div>
            <p className="text-xs text-slate-500 mt-1">Source for user deposit approvals & manual sends.</p>
          </div>
          <div>
            <label className="text-xs text-slate-400">Add to Admin Fund</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="fund-add-amount" type="number" min="0" className={field} placeholder="Amount" value={addAmt} onChange={(e) => setAddAmt(e.target.value)} />
              <button data-testid="fund-add-btn" disabled={busy === "add" || !addAmt} onClick={addFund}
                className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 font-semibold px-4 rounded-xl">
                {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Send to user's wallet</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="fund-send-code" className={`${field} w-28`} placeholder="User ID" value={send.referral_code} onChange={(e) => setSend({ ...send, referral_code: e.target.value })} />
              <input data-testid="fund-send-amount" type="number" min="0" className={`${field} w-24`} placeholder="Amount" value={send.amount} onChange={(e) => setSend({ ...send, amount: e.target.value })} />
              <select data-testid="fund-send-target" className={field} value={send.target} onChange={(e) => setSend({ ...send, target: e.target.value })}>
                <option value="deposit_wallet" className="bg-slate-900">Deposit Wallet</option>
                <option value="magic_card_wallet" className="bg-slate-900">Magic Card Wallet</option>
              </select>
              <button data-testid="fund-send-btn" disabled={busy === "send" || !send.amount || !send.referral_code || sendLookup.status !== "found"} onClick={sendFund}
                className="inline-flex items-center gap-1 border border-white/15 hover:border-emerald-500/40 text-white font-semibold px-4 rounded-xl">
                {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <NameHint state={sendLookup} testid="fund-send-name" />
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {["deposits", "withdrawals"].map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${
              tab === t ? "bg-emerald-500 text-slate-900" : "bg-white/5 text-slate-400 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {!list ? <Loader /> : (
        <div className="glass rounded-2xl p-2 sm:p-4" data-testid={`${tab}-table`}>
          {list.length === 0 ? <p className="text-sm text-slate-500 py-10 text-center">No {tab}.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                  <th className="py-3 px-3">User</th><th className="py-3 px-3">Amount</th>
                  {tab === "withdrawals" && <><th className="py-3 px-3">Fee</th><th className="py-3 px-3">Net</th></>}
                  {tab === "deposits" && <th className="py-3 px-3">Proof / Tx</th>}
                  <th className="py-3 px-3">Date</th><th className="py-3 px-3">Status</th><th className="py-3 px-3 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {list.map((it) => (
                    <tr key={it.id} className="border-b border-white/5 last:border-0">
                      <td className="py-3 px-3"><div className="text-white">{it.user_name}</div><div className="text-xs text-slate-500">{it.user_email}</div>{it.referral_code && <div className="text-[0.65rem] font-num text-cyan-300">{it.referral_code}</div>}</td>
                      <td className="py-3 px-3 font-num text-white">{fmt(it.amount)}</td>
                      {tab === "withdrawals" && <><td className="py-3 px-3 font-num text-rose-400">{fmt(it.fee)}</td><td className="py-3 px-3 font-num text-emerald-300">{fmt(it.net)}</td></>}
                      {tab === "deposits" && (
                        <td className="py-3 px-3">
                          {it.proof_path ? (
                            <a data-testid={`proof-${it.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.proof_path}`} target="_blank" rel="noreferrer"
                              className="text-cyan-300 hover:text-cyan-200 underline text-xs">View proof</a>
                          ) : <span className="text-xs text-slate-500">—</span>}
                          {it.tx_hash ? <div className="text-[0.6rem] text-slate-500 font-num truncate max-w-[140px]">{it.tx_hash}</div> : null}
                        </td>
                      )}
                      <td className="py-3 px-3 font-num text-slate-400">{new Date(it.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-3"><StatusPill status={it.status} /></td>
                      <td className="py-3 px-3 text-right">
                        {it.status === "pending" ? (
                          <div className="flex gap-2 justify-end">
                            <button data-testid={`approve-${it.id}`} onClick={() => act(`/admin/${tab}/${it.id}/approve`, "Approved")}
                              className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 px-3 py-1.5 rounded-lg text-xs font-semibold"><Check className="h-3.5 w-3.5" /> Approve</button>
                            <button data-testid={`reject-${it.id}`} onClick={() => reject(`/admin/${tab}/${it.id}/reject`)}
                              className="inline-flex items-center gap-1 bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 px-3 py-1.5 rounded-lg text-xs font-semibold"><X className="h-3.5 w-3.5" /> Reject</button>
                          </div>
                        ) : <span className="text-xs text-slate-500">Processed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
